import React, { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';
import api from '../services/api';
import { useUser } from './userContext';
import { WHATSAPP_META_UI_ENABLED } from '../config/whatsappMetaFeature';
import {
  mapMetaChannelsForComposer,
  mergeWhatsappConversations,
  mergeWhatsappMessages,
  whatsappContactId,
} from './whatsappChannelMerge';

const WhatsappZapiContext = createContext();

const CONVERSATIONS_LOAD_ERROR = 'Erro ao carregar conversas. Verifique sua conexão e tente novamente.';
const MESSAGES_LOAD_ERROR = 'Erro ao carregar mensagens. Verifique sua conexão e tente novamente.';

export const useWhatsappZapi = () => useContext(WhatsappZapiContext);

export const WhatsappZapiProvider = ({ children }) => {
  const { user } = useUser();
  const [isWhatsappZapiOpen, setIsWhatsappZapiOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [conversations, setConversations] = useState([]);
  const [selectedContact, setSelectedContact] = useState(null);
  const [messages, setMessages] = useState([]);
  const [availableInstances, setAvailableInstances] = useState([]);
  const [linkedCards, setLinkedCards] = useState([]);
  const [loadingConversations, setLoadingConversations] = useState(false);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [lastError, setLastError] = useState('');
  const [metaEnabled, setMetaEnabled] = useState(false);
  const [metaMarketingEnabled, setMetaMarketingEnabled] = useState(false);
  const [isMetaChannelOwner, setIsMetaChannelOwner] = useState(false);
  const conversationParamsRef = useRef({});
  const conversationsRequestRef = useRef(0);
  const messagesRequestRef = useRef(0);
  const linkedCardsRequestRef = useRef(0);
  const markReadRequestsRef = useRef(new Map());

  const loadMetaAvailability = useCallback(async () => {
    if (!user || !WHATSAPP_META_UI_ENABLED) {
      setMetaEnabled(false);
      return false;
    }
    try {
      const response = await api.get('/whatsapp-meta/config');
      const enabled = response.data?.enabled === true;
      setMetaEnabled(enabled);
      setMetaMarketingEnabled(enabled && response.data?.marketing_messages_api_enabled === true);
      return enabled;
    } catch (error) {
      setMetaEnabled(false);
      setMetaMarketingEnabled(false);
      return false;
    }
  }, [user]);

  const refreshUnreadCount = useCallback(async () => {
    if (!user) return;
    try {
      const [zapi, meta] = await Promise.all([
        api.get('/whatsapp-zapi/unread-count'),
        metaEnabled
          ? api.get('/whatsapp-meta/unread-count').catch(() => ({ data: { total: 0 } }))
          : Promise.resolve({ data: { total: 0 } }),
      ]);
      setUnreadCount(Number(zapi.data?.total || 0) + Number(meta.data?.total || 0));
    } catch (error) {
      setUnreadCount(0);
    }
  }, [metaEnabled, user]);

  const loadAvailableInstances = useCallback(async () => {
    if (!user) return [];
    try {
      const [zapi, meta] = await Promise.all([
        api.get('/whatsapp-zapi/instances/available-send'),
        metaEnabled
          ? api.get('/whatsapp-meta/channels/available-send').catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
      ]);
      const merged = [
        ...(zapi.data || []).map((instance) => ({ ...instance, provider: 'zapi' })),
        ...mapMetaChannelsForComposer(meta.data || []),
      ];
      setAvailableInstances(merged);
      return merged;
    } catch (error) {
      setAvailableInstances([]);
      return [];
    }
  }, [metaEnabled, user]);

  const loadConversations = useCallback(async (params = null, options = {}) => {
    if (!user) return [];
    const effectiveParams = params === null ? conversationParamsRef.current : params;
    if (params !== null) conversationParamsRef.current = params;
    const requestId = conversationsRequestRef.current + 1;
    conversationsRequestRef.current = requestId;
    if (!options.silent) setLoadingConversations(true);
    try {
      const [zapi, meta] = await Promise.all([
        api.get('/whatsapp-zapi/conversations', { params: effectiveParams }),
        metaEnabled
          ? api.get('/whatsapp-meta/conversations', { params: effectiveParams }).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
      ]);
      const data = mergeWhatsappConversations(
        (zapi.data || []).map((item) => ({ ...item, provider: item.provider || 'zapi' })),
        meta.data || [],
      );
      if (requestId === conversationsRequestRef.current) {
        setConversations(data);
        setLastError((current) => current.startsWith('Erro ao carregar conversas.') ? '' : current);
      }
      return data;
    } catch (error) {
      if (requestId === conversationsRequestRef.current && !options.silent) {
        setLastError(error.response?.data?.message || CONVERSATIONS_LOAD_ERROR);
      }
      return [];
    } finally {
      if (!options.silent) setLoadingConversations(false);
    }
  }, [metaEnabled, user]);

  const loadLinkedCards = useCallback(async (contactId) => {
    const requestId = linkedCardsRequestRef.current + 1;
    linkedCardsRequestRef.current = requestId;
    if (!contactId) {
      setLinkedCards([]);
      return [];
    }
    try {
      const response = await api.get(`/whatsapp-zapi/contacts/${contactId}/cards`);
      if (requestId === linkedCardsRequestRef.current) {
        setLinkedCards(response.data || []);
      }
      return response.data || [];
    } catch (error) {
      if (requestId === linkedCardsRequestRef.current) setLinkedCards([]);
      return [];
    }
  }, []);

  const loadMessages = useCallback(async (contactId, options = {}) => {
    if (!contactId) return [];
    const requestId = messagesRequestRef.current + 1;
    messagesRequestRef.current = requestId;
    if (!options.silent) setLoadingMessages(true);
    try {
      const [zapi, meta] = await Promise.all([
        api.get(`/whatsapp-zapi/conversations/${contactId}/messages`),
        metaEnabled
          ? api.get(`/whatsapp-meta/conversations/${contactId}/messages`).catch(() => ({ data: [] }))
          : Promise.resolve({ data: [] }),
      ]);
      const data = mergeWhatsappMessages(zapi.data || [], meta.data || []);
      if (requestId === messagesRequestRef.current) {
        setMessages(data);
        setLastError((current) => current.startsWith('Erro ao carregar mensagens.') ? '' : current);
      }
      return data;
    } catch (error) {
      if (requestId === messagesRequestRef.current && !options.silent) {
        setLastError(error.response?.data?.message || MESSAGES_LOAD_ERROR);
      }
      return [];
    } finally {
      if (!options.silent) setLoadingMessages(false);
    }
  }, [metaEnabled]);

  const markConversationRead = useCallback((contactId) => {
    if (!contactId) return Promise.resolve();
    const requestKey = String(contactId);
    const pending = markReadRequestsRef.current.get(requestKey);
    if (pending) return pending;

    conversationsRequestRef.current += 1;
    setConversations((current) => current.map((conversation) => (
      String(whatsappContactId(conversation)) === requestKey
        ? { ...conversation, unread_count: 0 }
        : conversation
    )));

    const request = (async () => {
      const readRequests = [api.post(`/whatsapp-zapi/conversations/${contactId}/read`)];
      if (metaEnabled) readRequests.push(api.post(`/whatsapp-meta/conversations/${contactId}/read`));
      const results = await Promise.allSettled(readRequests);
      const marked = results.some((result) => result.status === 'fulfilled');
      if (!marked) {
        await loadConversations(null, { silent: true });
        return;
      }
      await Promise.all([
        refreshUnreadCount(),
        loadConversations(null, { silent: true }),
      ]);
    })().finally(() => {
      markReadRequestsRef.current.delete(requestKey);
    });

    markReadRequestsRef.current.set(requestKey, request);
    return request;
  }, [loadConversations, metaEnabled, refreshUnreadCount]);

  const selectConversation = useCallback(async (conversation) => {
    const contactId = whatsappContactId(conversation);
    setSelectedContact(conversation);
    await Promise.all([
      loadMessages(contactId),
      loadLinkedCards(contactId),
      markConversationRead(contactId),
    ]);
  }, [loadLinkedCards, loadMessages, markConversationRead]);

  const openWhatsappZapi = useCallback(async (contact) => {
    setIsWhatsappZapiOpen(true);
    setLastError('');
    await Promise.all([loadAvailableInstances(), loadConversations({})]);
    if (contact) {
      await selectConversation(contact);
    }
  }, [loadAvailableInstances, loadConversations, selectConversation]);

  const closeWhatsappZapi = useCallback(() => {
    setIsWhatsappZapiOpen(false);
  }, []);

  const sendMessage = useCallback(async ({
    instanceId, type, text, mediaUrl, caption, fileName, extension, cardId,
    templateId, components, clientRequestId, replyToMessageId,
  }) => {
    const contactId = whatsappContactId(selectedContact);
    if (!contactId) throw new Error('Selecione uma conversa.');
    const isMeta = String(instanceId).startsWith('meta:');
    if (isMeta && type !== 'text' && type !== 'template') {
      throw new Error('No canal oficial, envie mídias selecionando um arquivo.');
    }
    const response = await api.post(isMeta ? '/whatsapp-meta/messages/send' : '/whatsapp-zapi/messages/send', {
      instanceId,
      channel_id: instanceId,
      contactId,
      contact_id: contactId,
      type,
      text,
      mediaUrl,
      caption,
      fileName,
      extension,
      cardId,
      card_id: cardId,
      template_id: templateId,
      components,
      client_request_id: clientRequestId,
      reply_to_message_id: replyToMessageId,
    });
    await loadMessages(contactId);
    await loadConversations();
    return response.data;
  }, [loadConversations, loadMessages, selectedContact]);

  const sendFileMessage = useCallback(async ({
    instanceId, type, file, caption, fileName, cardId, clientRequestId, replyToMessageId,
  }) => {
    const contactId = whatsappContactId(selectedContact);
    if (!contactId) throw new Error('Selecione uma conversa.');
    if (!file) throw new Error('Selecione um arquivo.');

    const formData = new FormData();
    formData.append('file', file);
    formData.append('instanceId', instanceId);
    formData.append('channel_id', instanceId);
    formData.append('contactId', contactId);
    formData.append('contact_id', contactId);
    formData.append('type', type);
    formData.append('caption', caption || '');
    formData.append('fileName', fileName || file.name);
    if (cardId) formData.append('cardId', cardId);
    if (clientRequestId) formData.append('client_request_id', clientRequestId);
    if (replyToMessageId) formData.append('reply_to_message_id', replyToMessageId);

    const isMeta = String(instanceId).startsWith('meta:');
    const response = await api.post(isMeta ? '/whatsapp-meta/messages/send-file' : '/whatsapp-zapi/messages/send-file', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    await loadMessages(contactId);
    await loadConversations();
    return response.data;
  }, [loadConversations, loadMessages, selectedContact]);

  const loadMetaTemplates = useCallback(async (instanceId) => {
    if (!metaEnabled || !String(instanceId).startsWith('meta:')) return [];
    const channelId = String(instanceId).replace(/^meta:/, '');
    const response = await api.get(`/whatsapp-meta/channels/${channelId}/templates`);
    return response.data || [];
  }, [metaEnabled]);

  const recordMetaOptIn = useCallback(async (contactId, payload) => {
    if (!metaEnabled) throw new Error('Canal oficial não habilitado.');
    const response = await api.post(`/whatsapp-meta/contacts/${contactId}/opt-in`, payload);
    return response.data;
  }, [metaEnabled]);

  const loadMetaOptInStatus = useCallback(async (contactId, channelId) => {
    if (!metaEnabled || !contactId || !channelId) return null;
    const response = await api.get(`/whatsapp-meta/contacts/${contactId}/opt-in-status`, {
      params: { channel_id: channelId },
    });
    return response.data || null;
  }, [metaEnabled]);

  const suggestAiReply = useCallback(async (instanceId) => {
    const contactId = whatsappContactId(selectedContact);
    if (!contactId) throw new Error('Selecione uma conversa.');
    if (!instanceId) throw new Error('Selecione o canal desta conversa.');
    const response = await api.post(`/whatsapp-zapi/conversations/${contactId}/ai-suggest-reply`, { instance_id: instanceId });
    return response.data;
  }, [selectedContact]);

  const createContact = useCallback(async (payload) => {
    const response = await api.post('/whatsapp-zapi/contacts', payload);
    await loadConversations();
    return response.data;
  }, [loadConversations]);

  const updateContact = useCallback(async (contactId, payload) => {
    const response = await api.put(`/whatsapp-zapi/contacts/${contactId}`, payload);
    setSelectedContact((current) => current && whatsappContactId(current) === contactId ? { ...current, ...response.data } : current);
    await loadConversations();
    return response.data;
  }, [loadConversations]);

  const archiveContact = useCallback(async (contactId) => {
    const normalizedContactId = String(contactId || '');
    if (!normalizedContactId) throw new Error('Selecione uma conversa.');

    const response = await api.delete(`/whatsapp-zapi/contacts/${normalizedContactId}`);
    conversationsRequestRef.current += 1;
    messagesRequestRef.current += 1;
    linkedCardsRequestRef.current += 1;
    setConversations((current) => current.filter(
      (conversation) => String(whatsappContactId(conversation)) !== normalizedContactId,
    ));
    setSelectedContact((current) => (
      current && String(whatsappContactId(current)) === normalizedContactId ? null : current
    ));
    setMessages([]);
    setLinkedCards([]);
    await Promise.all([
      refreshUnreadCount(),
      loadConversations(null, { silent: true }),
    ]);
    return response.data;
  }, [loadConversations, refreshUnreadCount]);

  const createCardFromContact = useCallback(async (contactId, payload) => {
    const response = await api.post(`/whatsapp-zapi/contacts/${contactId}/create-card`, payload);
    await loadLinkedCards(contactId);
    return response.data;
  }, [loadLinkedCards]);

  const linkContactToCard = useCallback(async (cardId, contactOrId, isPrimary = true) => {
    const payload = {
      is_primary: isPrimary,
    };

    if (typeof contactOrId === 'object' && contactOrId !== null) {
      const contactId = Number(contactOrId.contact_id || 0);
      const participanteId = Number(contactOrId.participante_id || contactOrId.participanteId || 0);
      if (contactId) payload.contact_id = contactId;
      if (participanteId) payload.participante_id = participanteId;
    } else {
      payload.contact_id = contactOrId;
    }

    const response = await api.post(`/whatsapp-zapi/cards/${cardId}/contacts`, {
      ...payload,
    });
    await loadLinkedCards(response.data?.contact_id || payload.contact_id);
    return response.data;
  }, [loadLinkedCards]);

  const getCardContacts = useCallback(async (cardId) => {
    const response = await api.get(`/whatsapp-zapi/cards/${cardId}/contacts`);
    return response.data || [];
  }, []);

  const unlinkContactFromCard = useCallback(async (cardId, contactId) => {
    const response = await api.delete(`/whatsapp-zapi/cards/${cardId}/contacts/${contactId}`);
    return response.data;
  }, []);

  const searchContacts = useCallback(async (term) => {
    const response = await api.get('/whatsapp-zapi/contacts/search', { params: { term } });
    return response.data || [];
  }, []);

  const listCompanies = useCallback(async (params = {}) => {
    const response = await api.get('/whatsapp-zapi/companies', { params });
    return response.data || { items: [], total: 0 };
  }, []);

  const searchCompanies = useCallback(async (term) => {
    const response = await api.get('/whatsapp-zapi/companies/search', { params: { term } });
    return response.data || [];
  }, []);

  const createCompany = useCallback(async (payload) => {
    const response = await api.post('/whatsapp-zapi/companies', payload);
    return response.data;
  }, []);

  const updateCompany = useCallback(async (companyId, payload) => {
    const response = await api.put(`/whatsapp-zapi/companies/${companyId}`, payload);
    return response.data;
  }, []);

  const getCompanyCards = useCallback(async (companyId) => {
    const response = await api.get(`/whatsapp-zapi/companies/${companyId}/cards`);
    return response.data || [];
  }, []);

  const getContactCompanies = useCallback(async (contactId) => {
    const response = await api.get(`/whatsapp-zapi/contacts/${contactId}/companies`);
    return response.data || [];
  }, []);

  const getCardCompanies = useCallback(async (cardId) => {
    const response = await api.get(`/whatsapp-zapi/cards/${cardId}/companies`);
    return response.data || [];
  }, []);

  const linkCompanyToCard = useCallback(async (cardId, companyId, isPrimary = true) => {
    const response = await api.post(`/whatsapp-zapi/cards/${cardId}/company`, {
      company_id: companyId,
      is_primary: isPrimary,
    });
    return response.data;
  }, []);

  const linkCompanyToContact = useCallback(async (companyId, contactId, isPrimary = false) => {
    const response = await api.post(`/whatsapp-zapi/companies/${companyId}/contacts`, {
      contact_id: contactId,
      is_primary: isPrimary,
    });
    return response.data;
  }, []);

  useEffect(() => {
    loadMetaAvailability();
  }, [loadMetaAvailability]);

  useEffect(() => {
    if (!user || !metaEnabled) {
      setIsMetaChannelOwner(false);
      return undefined;
    }
    let active = true;
    api.get('/whatsapp-meta/channels')
      .then((response) => {
        if (!active) return;
        const channels = Array.isArray(response.data) ? response.data : [];
        setIsMetaChannelOwner(channels.some((channel) => Number(channel.owner_user_id) === Number(user.id)));
      })
      .catch(() => {
        if (active) setIsMetaChannelOwner(false);
      });
    return () => { active = false; };
  }, [metaEnabled, user]);

  useEffect(() => {
    if (!user) {
      conversationsRequestRef.current += 1;
      messagesRequestRef.current += 1;
      linkedCardsRequestRef.current += 1;
      conversationParamsRef.current = {};
      setUnreadCount(0);
      setConversations([]);
      setSelectedContact(null);
      setMessages([]);
      setAvailableInstances([]);
      return;
    }

    refreshUnreadCount();
    const interval = setInterval(refreshUnreadCount, 8000);
    return () => clearInterval(interval);
  }, [refreshUnreadCount, user]);

  useEffect(() => {
    const contactId = whatsappContactId(selectedContact);
    if (!user || !isWhatsappZapiOpen || !contactId) return undefined;

    const refreshOpenConversation = () => Promise.all([
      loadMessages(contactId, { silent: true }),
      loadConversations(null, { silent: true }),
    ]);
    const interval = setInterval(refreshOpenConversation, 5000);
    return () => clearInterval(interval);
  }, [isWhatsappZapiOpen, loadConversations, loadMessages, selectedContact, user]);

  const value = {
    isMetaChannelOwner,
    isWhatsappZapiOpen,
    openWhatsappZapi,
    closeWhatsappZapi,
    unreadCount,
    refreshUnreadCount,
    conversations,
    loadConversations,
    selectedContact,
    selectConversation,
    markConversationRead,
    messages,
    loadMessages,
    availableInstances,
    loadAvailableInstances,
    linkedCards,
    loadLinkedCards,
    loadingConversations,
    loadingMessages,
    lastError,
    setLastError,
    sendMessage,
    sendFileMessage,
    suggestAiReply,
    createContact,
    updateContact,
    archiveContact,
    createCardFromContact,
    linkContactToCard,
    getCardContacts,
    unlinkContactFromCard,
    searchContacts,
    listCompanies,
    searchCompanies,
    createCompany,
    updateCompany,
    getCompanyCards,
    getContactCompanies,
    getCardCompanies,
    linkCompanyToCard,
    linkCompanyToContact,
    metaEnabled,
    metaMarketingEnabled,
    loadMetaAvailability,
    loadMetaTemplates,
    recordMetaOptIn,
    loadMetaOptInStatus,
  };

  return (
    <WhatsappZapiContext.Provider value={value}>
      {children}
    </WhatsappZapiContext.Provider>
  );
};
